/**
 * Calendar data transformers for RPC responses.
 *
 * These functions convert raw Supabase RPC responses into the date-keyed
 * calendar data structure used by the UI.
 */

const TAG = '[calendarTransform]';

/**
 * Helper: Calculate max test value from ovulation tests
 */
function getMaxTestValue(tests) {
  if (!tests || tests.length === 0) return null;
  const values = tests.map(t => t.value ?? 0);
  return Math.max(...values);
}

/**
 * Helper: Convert UTC timestamp to local date string (YYYY-MM-DD)
 * @param {string} timestamp - UTC timestamp string
 * @returns {string} Local date string in YYYY-MM-DD format
 */
function utcToLocalDateString(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Reassign ovulation tests to viewer's local timezone dates.
 *
 * The RPC returns tests grouped by UTC dates, but viewers should see tests
 * grouped by their local timezone. This function reassigns tests to the
 * correct local date keys.
 *
 * @param {object} calendarData - Date-keyed calendar data structure
 * @returns {object} Calendar data with tests reassigned to local dates
 */
export function reassignTestsToLocalTimezone(calendarData) {
  if (!calendarData) return calendarData;

  const result = {};
  const allTests = [];

  // Collect all tests from all days
  Object.keys(calendarData).forEach(dateStr => {
    const dayData = calendarData[dateStr];
    if (dayData.ovulationTests && dayData.ovulationTests.length > 0) {
      dayData.ovulationTests.forEach(test => {
        allTests.push({ ...test, originalDate: dateStr });
      });
    }
    // Copy non-test data to result
    result[dateStr] = { ...dayData };
    delete result[dateStr].ovulationTests;
    delete result[dateStr].maxTestValue;
  });

  // Reassign tests to local dates
  allTests.forEach(test => {
    const localDateStr = utcToLocalDateString(test.takenAt);
    if (!result[localDateStr]) {
      result[localDateStr] = {};
    }
    if (!result[localDateStr].ovulationTests) {
      result[localDateStr].ovulationTests = [];
    }
    result[localDateStr].ovulationTests.push(test);
  });

  // Recalculate maxTestValue for each day
  Object.keys(result).forEach(dateStr => {
    if (result[dateStr].ovulationTests && result[dateStr].ovulationTests.length > 0) {
      result[dateStr].maxTestValue = getMaxTestValue(result[dateStr].ovulationTests);
    }
  });

  return result;
}

/**
 * Transform IP calendar data from get_ip_calendar RPC.
 *
 * Converts RPC response into date-keyed structure:
 * { "YYYY-MM-DD": { ipData: {...}, note, ovulationTests, maxTestValue } }
 * Then reassigns tests to viewer's local timezone.
 *
 * @param {object} rpcData - Raw RPC response from get_ip_calendar
 * @returns {object} Date-keyed calendar data structure
 */
export function transformIpCalendarData(rpcData) {
  const result = {};
  if (!rpcData) return result;

  const days = rpcData.days || [];

  days.forEach(day => {
    const dateStr = day.day;
    result[dateStr] = {};

    // Period data
    if (day.is_period) {
      result[dateStr].ipData = { periodDay: true };
    }
    if (day.period_start) {
      result[dateStr].ipData = result[dateStr].ipData || {};
      result[dateStr].ipData.periodStart = true;
    }
    if (day.period_end) {
      result[dateStr].ipData = result[dateStr].ipData || {};
      result[dateStr].ipData.periodEnd = true;
    }
    if (day.ovulation_actual) {
      result[dateStr].ipData = result[dateStr].ipData || {};
      result[dateStr].ipData.ovulationActual = true;
    }
    if (day.ovulation_prediction) {
      result[dateStr].ipData = result[dateStr].ipData || {};
      result[dateStr].ipData.ovulationPrediction = true;
    }
    if (day.insemination_day) {
      result[dateStr].ipData = result[dateStr].ipData || {};
      result[dateStr].ipData.inseminationDay = true;
    }

    // Note
    if (day.note) {
      result[dateStr].note = day.note;
    }

    // Ovulation tests
    if (day.ovulation_tests && day.ovulation_tests.length > 0) {
      result[dateStr].ovulationTests = day.ovulation_tests.map(test => ({
        id: test.id,
        imageUrl: test.photo_url || '',
        value: test.value,
        level: test.level,
        peak: test.peak,
        takenAt: test.taken_at,
      }));
      result[dateStr].maxTestValue = getMaxTestValue(day.ovulation_tests);
    }
  });

  // Reassign tests to viewer's local timezone
  return reassignTestsToLocalTimezone(result);
}

/**
 * Transform donor calendar data from get_donor_calendar RPC.
 *
 * Converts RPC response into date-keyed structure:
 * { "YYYY-MM-DD": { sharedOvulationPredictions, events, note } }
 *
 * @param {object} rpcData - Raw RPC response from get_donor_calendar
 * @returns {object} Date-keyed calendar data structure
 */
export function transformDonorCalendarData(rpcData) {
  const result = {};
  if (!rpcData) return result;

  const sharedOvulations = rpcData.shared_ovulations || [];
  const events = rpcData.events || [];
  const days = rpcData.days || [];

  // Process donor's own calendar day notes
  days.forEach(d => {
    const dateStr = d.day;
    if (!result[dateStr]) {
      result[dateStr] = {};
    }
    if (d.note) {
      result[dateStr].note = d.note;
    }
  });

  // Process shared ovulations
  // Deduplicate by owner_id - only show one instance per user per day
  const seenOwners = new Map(); // Map of dateStr -> Set of owner_ids seen

  sharedOvulations.forEach(ov => {
    const dateStr = ov.day;
    if (!result[dateStr]) {
      result[dateStr] = { sharedOvulationPredictions: [] };
    }
    if (!result[dateStr].sharedOvulationPredictions) {
      result[dateStr].sharedOvulationPredictions = [];
    }

    // Deduplicate: only add if we haven't seen this owner for this date
    if (!seenOwners.has(dateStr)) {
      seenOwners.set(dateStr, new Set());
    }
    const ownersForDate = seenOwners.get(dateStr);

    if (!ownersForDate.has(ov.owner_id)) {
      ownersForDate.add(ov.owner_id);
      result[dateStr].sharedOvulationPredictions.push({
        ownerId: ov.owner_id,
        ownerName: ov.owner_name,
        ownerImages: ov.owner_images,
        ovulationActual: ov.ovulation_actual,
        ovulationPrediction: ov.ovulation_prediction,
      });
    }
  });

  // Build contact profiles map from events
  const contactProfiles = {};
  events.forEach(event => {
    if (event.contact_id && event.contact_name) {
      contactProfiles[event.contact_id] = {
        name: event.contact_name,
        images: event.contact_images || []
      };
    }
  });

  // Process events
  events.forEach(event => {
    const dateStr = event.day;
    if (!result[dateStr]) {
      result[dateStr] = { events: [] };
    }
    if (!result[dateStr].events) {
      result[dateStr].events = [];
    }
    result[dateStr].events.push({
      id: event.id,
      date: event.day,
      title: event.title,
      user: null,
      contact: event.contact_id,
      contactProfile: contactProfiles[event.contact_id] || null,
      note: event.note || '',
      status: event.status || 'pending',
    });
  });

  return result;
}
